import { Box, Flex, FormLabel, Image, Spinner, Text, Tooltip } from '@chakra-ui/react';
import { useEffect, type JSX } from 'react';

import { loadOutdoorPhotos, selectOutdoorPhotos, selectOutdoorPhotosDealId, selectOutdoorPhotosStatus, useAppDispatch, useAppSelector } from '@state';

export type OutdoorPhotosPickerProps = {
	onSelect: (base64: string) => void;
	dealId: string | null;
};

export const OutdoorPhotosPicker = (props: OutdoorPhotosPickerProps): JSX.Element | null => {
	const { onSelect, dealId } = props;
	const dispatch = useAppDispatch();
	const photos = useAppSelector(selectOutdoorPhotos);
	const status = useAppSelector(selectOutdoorPhotosStatus);
	const cachedDealId = useAppSelector(selectOutdoorPhotosDealId);

	useEffect(() => {
		if (dealId && dealId !== cachedDealId && status !== 'loading') {
			dispatch(loadOutdoorPhotos(dealId));
		}
	}, [dealId, cachedDealId, status, dispatch]);

	if (!dealId) return null;

	return (
		<Box mt={2}>
			<FormLabel fontSize="small">Outdoor Area Photos:</FormLabel>
			{status === 'loading' && (
				<Flex justify="center" p={3}>
					<Spinner size="sm" />
				</Flex>
			)}
			{status === 'loaded' && photos.length === 0 && (
				<Text fontSize="xs" color="gray.500">No outdoor photos found</Text>
			)}
			{status === 'error' && (
				<Text fontSize="xs" color="red.500">Failed to load photos</Text>
			)}
			{status === 'loaded' && photos.length > 0 && (
				<Flex gap={2} overflowX="auto" pb={1}>
					{photos.map((photo, index) => (
						<Tooltip key={index} label={photo.filename} fontSize="xs">
							<Box
								minW="60px"
								h="60px"
								borderRadius="md"
								overflow="hidden"
								cursor="pointer"
								border="2px solid"
								borderColor="gray.200"
								_hover={{ borderColor: 'blue.400', transform: 'scale(1.05)' }}
								transition="all 0.15s"
								onClick={() => onSelect(photo.base64)}
							>
								<Image
									src={photo.base64}
									alt={photo.filename}
									objectFit="cover"
									w="100%"
									h="100%"
								/>
							</Box>
						</Tooltip>
					))}
				</Flex>
			)}
		</Box>
	);
};
